import { createSlice } from '@reduxjs/toolkit';

import { TransportTypeEnum } from '@shared/enums/garage.enums';

import { GarageCreateItemStepsEnum } from '../enum/garageCreateItemSteps.enum';
import { WayToEnterCarInformationEnum } from '../enum/wayToEnterCarInfo.enum';

import type {
  Insurances,
  Licences,
  RootGarageItemsData,
  RootGarageItemsDataStep,
  VehicleStickers,
} from './types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { MediaType } from '@shared/models/media.model';

const formStapperArr = [
  GarageCreateItemStepsEnum.preStepTransportType,
  GarageCreateItemStepsEnum.preStepSelectMethod,
  GarageCreateItemStepsEnum.preStepAI,
  GarageCreateItemStepsEnum.generalBase,
  GarageCreateItemStepsEnum.generalSecond,
  GarageCreateItemStepsEnum.generalThird,
  GarageCreateItemStepsEnum.coverPhoto,
  GarageCreateItemStepsEnum.insuranceBase,
  GarageCreateItemStepsEnum.insuranceSecond,
  GarageCreateItemStepsEnum.addVehicleRegistration,
  GarageCreateItemStepsEnum.addLpRegistration,
];

const initialState: any = {
  formStepper: {
    [GarageCreateItemStepsEnum.preStepTransportType]: {
      stepId: GarageCreateItemStepsEnum.preStepTransportType,
      isDisabled: false,
      isPassed: false,
      isSkip: false,
      id: 0,
    },
    [GarageCreateItemStepsEnum.preStepSelectMethod]: {
      stepId: GarageCreateItemStepsEnum.preStepSelectMethod,
      isDisabled: false,
      isPassed: false,
      isSkip: false,
      id: 1,
    },
    [GarageCreateItemStepsEnum.preStepAI]: {
      stepId: GarageCreateItemStepsEnum.preStepAI,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 2,
    },
    [GarageCreateItemStepsEnum.generalBase]: {
      stepId: GarageCreateItemStepsEnum.generalBase,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 3,
    },
    [GarageCreateItemStepsEnum.generalSecond]: {
      stepId: GarageCreateItemStepsEnum.generalSecond,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 4,
    },
    [GarageCreateItemStepsEnum.generalThird]: {
      stepId: GarageCreateItemStepsEnum.generalThird,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 5,
    },
    [GarageCreateItemStepsEnum.coverPhoto]: {
      stepId: GarageCreateItemStepsEnum.coverPhoto,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 6,
    },
    [GarageCreateItemStepsEnum.insuranceBase]: {
      stepId: GarageCreateItemStepsEnum.insuranceBase,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 7,
    },
    [GarageCreateItemStepsEnum.insuranceSecond]: {
      stepId: GarageCreateItemStepsEnum.insuranceSecond,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 8,
    },

    [GarageCreateItemStepsEnum.addVehicleRegistration]: {
      stepId: GarageCreateItemStepsEnum.addVehicleRegistration,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 9,
    },
    [GarageCreateItemStepsEnum.addLpRegistration]: {
      stepId: GarageCreateItemStepsEnum.addLpRegistration,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 10,
    },
    [GarageCreateItemStepsEnum.share]: {
      stepId: GarageCreateItemStepsEnum.share,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 11,
    },
    [GarageCreateItemStepsEnum.congratulations]: {
      stepId: GarageCreateItemStepsEnum.congratulations,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 12,
    },
  },
  currentStep: GarageCreateItemStepsEnum.preStepTransportType,
  aiScanDocument: null,
  transportType: TransportTypeEnum.car,
  wayToEnterTransportInfo: WayToEnterCarInformationEnum.ai,
  isCanChangeCurrentStep: true,
  recognizedData: null,
  draftItem: null,
};

export const garageCreateTransportSlice = createSlice({
  name: 'garageCreateItem',
  initialState,
  reducers: {
    setGarageCreateTransportType: (state, action: PayloadAction<TransportTypeEnum>) => {
      state.transportType = action.payload;
    },
    setGarageCreateDraftItem: (state, action: PayloadAction<RootGarageItemsData | null>) => {
      state.draftItem = action.payload;
      if (action.payload) {
        state.transportType = action.payload.transport_type;
        state.aiScanDocument = action.payload.scan_document;
      }
    },
    setGarageCreateDraftItemInsurances: (state, action: PayloadAction<Insurances | null>) => {
      state.draftItem.insurances = [action.payload];
    },
    setGarageCreateDraftItemLicences: (state, action: PayloadAction<Licences | null>) => {
      state.draftItem.licenses = [action.payload];
    },
    setGarageCreateDraftItemStickers: (state, action: PayloadAction<VehicleStickers | null>) => {
      state.draftItem.stickers = [action.payload];
    },
    updateGarageCreateDraftItem: (state, action: PayloadAction<any | null>) => {
      state.draftItem = { ...state.draftItem, ...action.payload };
    },
    setGarageCreateDraftItemCoverPhoto: (state, action: PayloadAction<MediaType | null>) => {
      state.draftItem.cover_photo = action.payload;
    },
    setGarageCreateTransportRecognizedData: (state, action: PayloadAction<any>) => {
      state.recognizedData = action.payload;
    },
    setGarageCreateTransportCurrentStep: (state, action: PayloadAction<GarageCreateItemStepsEnum>) => {
      state.currentStep = action.payload;
      state.isCanChangeCurrentStep = true;
    },
    setGarageCreateTransportAIScanDocument: (state, action: PayloadAction<MediaType | null>) => {
      state.aiScanDocument = action.payload;
    },
    updateGarageCreateTransportAIScanDocument: (state, action: PayloadAction<any>) => {
      state.aiScanDocument = {
        ...state.aiScanDocument,
        recognition_data: {
          ...state.aiScanDocument.recognition_data,
          data: { ...state.aiScanDocument.recognition_data.data, ...action.payload },
        },
      };
    },
    setGarageCreateTransportWayToEnterInfo: (state, action: PayloadAction<WayToEnterCarInformationEnum>) => {
      state.wayToEnterTransportInfo = action.payload;
    },
    setGarageCreateTransportCanChangeCurrentStep: (state, action: PayloadAction<boolean>) => {
      state.isCanChangeCurrentStep = action.payload;
    },
    setGarageCreateTransportFormStepPassed: (
      state,
      action: PayloadAction<{ step: GarageCreateItemStepsEnum; isPassed?: boolean }>,
    ) => {
      const { step, isPassed = true } = action.payload;
      if (state.formStepper[step]) {
        state.formStepper[step].isPassed = isPassed;
      }
    },
    setGarageCreateTransportFormStepDisabled: (
      state,
      action: PayloadAction<{ step: GarageCreateItemStepsEnum; isDisabled?: boolean }>,
    ) => {
      const { step, isDisabled = true } = action.payload;
      if (state.formStepper[step]) {
        state.formStepper[step].isDisabled = isDisabled;
      }
    },
    setGarageCreateTransportFormStepSkip: (
      state,
      action: PayloadAction<{ step: GarageCreateItemStepsEnum; isSkip?: boolean }>,
    ) => {
      const { step, isSkip = true } = action.payload;
      if (state.formStepper[step]) {
        state.formStepper[step].isSkip = isSkip;
      }
    },

    changeGarageCreateTransportFormStepperFromDraftItem: (
      state,
      action: PayloadAction<RootGarageItemsDataStep[]>,
    ) => {
      const enabledSteps = new Set([
        GarageCreateItemStepsEnum.preStepTransportType,
        GarageCreateItemStepsEnum.preStepSelectMethod,
        GarageCreateItemStepsEnum.preStepAI,
      ]);
      const isIncludedAiStep = action.payload.some(
        (item) => item.step === GarageCreateItemStepsEnum.preStepAI,
      );

      state.wayToEnterTransportInfo = isIncludedAiStep
        ? WayToEnterCarInformationEnum.ai
        : WayToEnterCarInformationEnum.manual;

      action.payload.forEach(({ step }) => {
        const currentStep = state.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });

      const lastStepInPayload = action.payload[action.payload.length - 1];
      if (lastStepInPayload) {
        state.currentStep =
          formStapperArr[formStapperArr.findIndex((item) => item === lastStepInPayload.step) + 1];
      }
    },

    changeGarageCreateTransportFormStepperPassedDisableSteps: (
      state,
      action: PayloadAction<{
        enabledSteps: GarageCreateItemStepsEnum[];
        passedStep: RootGarageItemsDataStep[];
      }>,
    ) => {
      const enabledSteps = new Set(action.payload.enabledSteps);

      action.payload.passedStep.forEach(({ step }) => {
        const currentStep = state.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });
    },

    resetCreateGarageItemStore: () => {
      return initialState;
    },
  },
});
export const {
  setGarageCreateTransportRecognizedData,
  resetCreateGarageItemStore,
  setGarageCreateDraftItem,
  setGarageCreateTransportAIScanDocument,
  setGarageCreateTransportCurrentStep,
  setGarageCreateTransportType,
  setGarageCreateTransportWayToEnterInfo,
  setGarageCreateTransportCanChangeCurrentStep,
  setGarageCreateTransportFormStepDisabled,
  setGarageCreateTransportFormStepPassed,
  changeGarageCreateTransportFormStepperFromDraftItem,
  changeGarageCreateTransportFormStepperPassedDisableSteps,
  setGarageCreateDraftItemCoverPhoto,
  setGarageCreateDraftItemInsurances,
  setGarageCreateTransportFormStepSkip,
  updateGarageCreateDraftItem,
  setGarageCreateDraftItemLicences,
  setGarageCreateDraftItemStickers,
  updateGarageCreateTransportAIScanDocument,
} = garageCreateTransportSlice.actions;
export default garageCreateTransportSlice.reducer;
